import React, { useState, useEffect, useRef } from "react"
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Label,
  Button,
  Form,
  Alert,
} from "reactstrap"
import { get, put, post } from "../../helpers/api_helper"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { useParams, useNavigate } from "react-router-dom"
import {
  UPDATE_EMERGENCY_TYPES,
  LANGUAGES_SEARCH,
  SHOW_EMERGENCY_TYPES,
  UPLOAD_FILE,
} from "helpers/api_endpoints"
import Select from "react-select"
import { Trans } from "react-i18next"

document.title = "Edit Emergency Type"

function EditEmergencyType() {
  const [loading, setLoading] = useState(true)
  const params = useParams()
  const [id, setId] = useState(params.id)
  const [title, setTitle] = useState("")
  const [valued, setValued] = useState({ title: [] })
  const [icon, setIcon] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [languages, setLanguages] = useState([])
  const [errorMsg, seterrorMsg] = useState("")
  const fileInputRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    let isMounted = true
    const getEmergencyType = async () => {
      setLoading(true)
      try {
        if (!params.id) {
          throw new Error("No emergency type ID provided")
        }

        const response = await get(`${SHOW_EMERGENCY_TYPES}/${params.id}`)
        const emergencyType = response.data

        const parsedTitle =
          typeof emergencyType.title === "string"
            ? JSON.parse(emergencyType.title)
            : emergencyType.title

        if (emergencyType.id) {
          setId(emergencyType.id)
        }

        setValued({
          ...emergencyType,
          title: parsedTitle.title || [],
        })

        if (emergencyType.icon) {
          setIcon(emergencyType.icon);
        }

        setLoading(false)
      } catch (error) {
        console.error("Error in getEmergencyType:", error)
        seterrorMsg(error.message || "Failed to load emergency type")
        setLoading(false)
      }
    }

    const getLanguages = async () => {
      setLoading(true)
      try {
        const response = await post(`${LANGUAGES_SEARCH}`, { status: 1 })
        const languages = response.data
        setLanguages(languages)
        setLoading(false)
      } catch (error) {
        console.log(error)
        seterrorMsg(error.message)
      }
    }
    getLanguages()
    getEmergencyType()

    return () => {
      isMounted = false
    }
  }, [params.id])

  const handleSubmit = e => {
    e.preventDefault()

    if (!id) {
      seterrorMsg("Error: No emergency type ID available")
      return
    }

    const payload = {
      id,
      title: JSON.stringify({
        title: valued.title,
      }),
      ...(icon && { icon }),
    }

    put(UPDATE_EMERGENCY_TYPES, payload)
      .then(response => {
        if (response.code === 200) {
          navigate("/emergency-types", {
            state: { successMsg: "Emergency Type Updated Successfully" },
          })
        }
      })
      .catch(error => {
        console.error("Update error:", error)
        seterrorMsg(error.message || "Failed to update emergency type")
      })
  }

  const handleTitle = (languageId, value) => {
    const currentTitles = Array.isArray(valued.title) ? [...valued.title] : []

    const existingIndex = currentTitles.findIndex(
      el => el[languageId] !== undefined
    )

    if (existingIndex === -1) {
      const newEntry = { [languageId]: value }
      currentTitles.push(newEntry)
    } else {
      currentTitles[existingIndex][languageId] = value
    }

    setValued({ ...valued, title: currentTitles })
    setTitle(JSON.stringify(valued))
  }

  const clearPhoto = () => {
    setIcon(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }

  const handleFileUpload = event => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 1 * 1024 * 1024) {
      seterrorMsg("File size is larger than 1MB");
      setTimeout(() => seterrorMsg(""), 1700);
      return;
    }
    if (file.type !== "image/jpeg" && file.type !== "image/png") {
      seterrorMsg("File type is incorrect");
      setTimeout(() => seterrorMsg(""), 1700);
      return;
    }

    setSelectedFile(file);

    post(
      `${UPLOAD_FILE}`,
      { file, type: "emergency_type_icon", fullPath: "true" },
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    )
      .then(response => {
        const data = response.data;
        setIcon(data.path);
      })
      .catch(error => {
        console.error("Upload error:", error);
        seterrorMsg("File upload failed.");
        setSelectedFile(null); // Clear preview on error
      });
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title="Emergency Sub Type"
            breadcrumbItem="Edit Emergency Sub Type"
          />
          <Form className="form-horizontal" onSubmit={handleSubmit}>
            <Row>
              <Col xl={12}>
                <Card>
                  <CardBody>
                    <Row>
                      {!params.id ? (
                        <Alert color="danger">
                          Error: No emergency type ID provided in the URL
                        </Alert>
                      ) : null}
                      {errorMsg ? (
                        <Alert color="danger">{errorMsg}</Alert>
                      ) : null}
                      {languages.map((language, index) => (
                        <Row key={index}>
                          <div className="form-group col-md-4 mt-2">
                            <Label className="form-label">
                              <Trans>Title ({language.name})</Trans>
                            </Label>
                            <Input
                              name="title"
                              value={
                                valued.title && Array.isArray(valued.title)
                                  ? (() => {
                                      const langEntry = valued.title.find(
                                        el => el[language.id] !== undefined
                                      )
                                      return langEntry
                                        ? langEntry[language.id]
                                        : ""
                                    })()
                                  : ""
                              }
                              className="form-control"
                              placeholder="Enter Title"
                              type="text"
                              onChange={e =>
                                handleTitle(language.id, e.target.value)
                              }
                              required={language.id === 1 || language.id === 2}
                            />
                          </div>
                        </Row>
                      ))}
                      <Row>
                        <div className="form-group col-md-4 mt-2">
                          <Label className="form-label">
                            <Trans>Icon</Trans>
                          </Label>
                          <div className="form-group col-md-12 mt-2">
                            <Label className="form-label">
                              <Trans>Icon</Trans>
                            </Label>
                            <input
                              ref={fileInputRef}
                              className="form-control"
                              type="file"
                              onChange={handleFileUpload}
                            />
                          </div>
                          {(selectedFile || icon) && (
                            <div>
                              <input
                                className="form-control"
                                type="button"
                                value="X"
                                onClick={clearPhoto}
                              />
                              <img src={selectedFile ? URL.createObjectURL(selectedFile) : icon} width="170" height="270" alt="Icon Preview" />
                            </div>
                          )}
                        </div>
                      </Row>
                    </Row>
                    <div className="text-center  mt-4">
                      <Button type="submit" color="danger">
                        <Trans>Submit</Trans>
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
    </React.Fragment>
  )
}

export default EditEmergencyType
